import { useEffect, useId, useRef } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';
import { FilterDefinition } from 'src/uniform/contenttypes/filterDefinition';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import type { SubFilterObject, UniformFilterObject } from '../utils/types';

/**
 * Marker attribute placed on every element that should count as "inside" a
 * filter control for outside-click detection. The parent uses this to decide
 * whether to commit + close open dropdowns.
 */
export const FILTER_DROPDOWN_DATA_ATTR = 'data-filter-dropdown';

export interface FilterControlProps {
	readonly filter: FilterDefinition;
	readonly activeCount: number;
	readonly isOpen: boolean;
	readonly subFilter: UniformFilterObject[string] | undefined;
	readonly labelMap: Record<string, string> | undefined;
	readonly onButtonClick: () => void;
	readonly onOptionToggle: (optionId: string) => void;
	/** Clears every selection for this filter (the "All" option). */
	readonly onSelectAll: () => void;
}

const sanitizeForId = (value: string) => value.replace(/[^\w-]/g, '-');

/** "Item" -> "items". Lowercases and pluralizes the filter label for "All items". */
const buildAllLabelNoun = (filterLabel: string) => {
	const lower = filterLabel.toLowerCase();
	if (lower.endsWith('s')) return lower;
	if (/[^aeiou]y$/.test(lower)) return `${lower.slice(0, -1)}ies`;
	return `${lower}s`;
};

const buildPanelId = (filterId: string, instanceId: string) =>
	`filter-panel-${sanitizeForId(filterId)}-${instanceId}`;

/**
 * One filter's full UI: a clickable pill-style trigger button and the
 * floating options panel that appears beneath it when open.
 */
export const FilterControl = ({
	filter,
	activeCount,
	isOpen,
	subFilter,
	labelMap,
	onButtonClick,
	onOptionToggle,
	onSelectAll,
}: FilterControlProps) => {
	const intl = useIntl();
	const filterLabel = filter.label ?? filter.id;
	const instanceId = useId();
	const panelId = buildPanelId(filter.id, instanceId);
	const controlRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const onButtonClickRef = useRef(onButtonClick);
	onButtonClickRef.current = onButtonClick;

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				event.key !== 'Escape' ||
				event.defaultPrevented ||
				!(event.target instanceof Node) ||
				!controlRef.current?.contains(event.target)
			) {
				return;
			}
			event.preventDefault();
			onButtonClickRef.current();
			triggerRef.current?.focus();
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen]);
	// An authored (localizable) label wins; the derived "All {label}" is only the fallback.
	const allLabel =
		filter.selectAllLabel ||
		intl.formatMessage(
			{ id: 'uniformFilterableGallery.allOption', defaultMessage: 'All {filterLabel}' },
			{ filterLabel: buildAllLabelNoun(filterLabel) },
		);
	// Collapsed label: "All items" with no selection, the option's own label with
	// exactly one, otherwise the filter label plus a count badge.
	const selectedOptionId =
		activeCount === 1
			? Object.keys(subFilter ?? {}).find((id) => subFilter?.[id]?.committedActive)
			: undefined;
	const collapsedLabel = getCollapsedLabel({
		activeCount,
		allLabel,
		filterLabel,
		selectedLabel:
			selectedOptionId === undefined
				? undefined
				: (labelMap?.[selectedOptionId] ?? selectedOptionId),
	});
	// Fold the count into the accessible name; the visible badge is aria-hidden.
	const accessibleName =
		activeCount > 1 ? `${filterLabel}, ${activeCount} selected` : collapsedLabel;

	return (
		<div ref={controlRef}>
			<button
				ref={triggerRef}
				type="button"
				css={[triggerButtonStyle, isOpen && triggerButtonOpenStyle]}
				aria-label={accessibleName}
				aria-expanded={isOpen}
				aria-controls={panelId}
				onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
					event.preventDefault();
					onButtonClick();
				}}
				data-product-key="uniform"
				data-event="clicked"
				data-event-component="button"
				data-event-container="searchWithFilters"
				data-label={filterLabel}
			>
				<span css={labelGroupStyle}>
					<span css={labelTextStyle}>{collapsedLabel}</span>
					{activeCount > 1 && (
						<span css={activeCountBadgeStyle} data-testid="active-count-badge" aria-hidden="true">
							{activeCount}
						</span>
					)}
				</span>
				<DropdownArrow isOpen={isOpen} />
			</button>
			{isOpen && subFilter && (
				<OptionsPanel
					panelId={panelId}
					isSingleSelect={filter.selectionMode === 'single'}
					groupLabel={filterLabel}
					filterId={filter.id}
					options={subFilter}
					onToggle={onOptionToggle}
					labelMap={labelMap}
					allLabel={allLabel}
					isAllSelected={activeCount === 0}
					onSelectAll={onSelectAll}
				/>
			)}
		</div>
	);
};

// ---------------------------------------------------------------------------
// Internal sub-components (private to this file)
// ---------------------------------------------------------------------------

const getCollapsedLabel = ({
	activeCount,
	allLabel,
	filterLabel,
	selectedLabel,
}: {
	readonly activeCount: number;
	readonly allLabel: string;
	readonly filterLabel: string;
	readonly selectedLabel: string | undefined;
}) => {
	if (activeCount === 0) return allLabel;
	if (activeCount === 1 && selectedLabel !== undefined) return selectedLabel;
	return filterLabel;
};

/**
 * Matches the Figma "carat-down" glyph exactly (node 933:335678) — Atlaskit's
 * stock chevron icon has a different, thinner glyph that reads as too small
 * at this size.
 */
const DropdownArrow = ({ isOpen }: { readonly isOpen: boolean }) => (
	<span css={[dropdownArrowStyle, isOpen && dropdownArrowRotatedStyle]}>
		<svg
			width="13.213"
			height="8.128"
			viewBox="0 0 13.213 8.12828"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			focusable="false"
		>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M0.282391 2.04788C-0.0941303 1.65992 -0.0941305 1.04295 0.282391 0.654991L0.623465 0.303554C1.01627 -0.101184 1.66588 -0.101185 2.05868 0.303553L6.60648 4.98954L11.1543 0.303554C11.5471 -0.101184 12.1967 -0.101185 12.5895 0.303554L12.9306 0.654991C13.3071 1.04295 13.3071 1.65992 12.9306 2.04788L7.32409 7.82473C6.93129 8.22946 6.28168 8.22946 5.88887 7.82473L0.282391 2.04788Z"
				fill="currentColor"
			/>
		</svg>
	</span>
);

interface OptionsPanelProps {
	readonly panelId: string;
	readonly isSingleSelect: boolean;
	readonly groupLabel: string;
	readonly filterId: string;
	readonly options: SubFilterObject;
	readonly onToggle: (optionId: string) => void;
	/** Optional map of option value → display label. Falls back to the value itself. */
	readonly labelMap: Record<string, string> | undefined;
	readonly allLabel: string;
	readonly isAllSelected: boolean;
	readonly onSelectAll: () => void;
}

const OptionsPanel = ({
	panelId,
	isSingleSelect,
	groupLabel,
	filterId,
	options,
	onToggle,
	labelMap,
	allLabel,
	isAllSelected,
	onSelectAll,
}: OptionsPanelProps) => (
	<div
		id={panelId}
		role={isSingleSelect ? 'radiogroup' : 'group'}
		aria-label={groupLabel}
		css={panelStyle}
		className={filterId}
		{...{ [FILTER_DROPDOWN_DATA_ATTR]: '' }}
	>
		<div css={optionListStyle}>
			<OptionCheckbox
				inputType={isSingleSelect ? 'radio' : 'checkbox'}
				groupName={panelId}
				optionId="all"
				label={allLabel}
				checked={isAllSelected}
				inputId={buildOptionInputId(panelId, -1)}
				onToggle={onSelectAll}
			/>
			{Object.keys(options).map((optionId, index) => (
				<OptionCheckbox
					key={optionId}
					inputType={isSingleSelect ? 'radio' : 'checkbox'}
					groupName={panelId}
					optionId={optionId}
					label={labelMap?.[optionId] ?? optionId}
					checked={options[optionId]?.stagedActive ?? false}
					inputId={buildOptionInputId(panelId, index)}
					onToggle={onToggle}
				/>
			))}
		</div>
	</div>
);

const buildOptionInputId = (panelId: string, index: number) => `${panelId}-option-${index}`;

interface OptionCheckboxProps {
	readonly inputType: 'checkbox' | 'radio';
	readonly groupName: string;
	readonly optionId: string;
	readonly label: string;
	readonly checked: boolean;
	readonly inputId: string;
	readonly onToggle: (optionId: string) => void;
}

// Native inputs preserve checkbox/radio keyboard behavior within the existing WAC treatment.
const OptionCheckbox = ({
	inputType,
	groupName,
	optionId,
	label,
	checked,
	inputId,
	onToggle,
}: OptionCheckboxProps) => (
	<label htmlFor={inputId} css={optionLabelStyle}>
		<input
			id={inputId}
			type={inputType}
			name={inputType === 'radio' ? groupName : undefined}
			checked={checked}
			onChange={() => onToggle(optionId)}
			css={[inputType === 'checkbox' && hiddenCheckboxStyle]}
		/>
		{inputType === 'checkbox' && (
			<span css={checkboxStyle} className={checked ? 'checked' : ''}>
				<span css={[checkmarkStyle, checked && checkmarkVisibleStyle]}>
					<svg
						width="11.17"
						height="7.26"
						viewBox="0 0 11.1698 7.25995"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
						focusable="false"
					>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M10.89 0.295063C11.2824 0.696189 11.2592 1.34412 10.839 1.71607L4.85995 7.00873C4.46092 7.36195 3.85499 7.3402 3.48232 6.95927L0.277834 3.68379C-0.0926113 3.30513 -0.0926115 2.69987 0.277834 2.32122C0.659939 1.93065 1.28848 1.93065 1.67059 2.32122L4.14761 4.85312L9.55101 0.235313C9.94354 -0.100149 10.5289 -0.0740284 10.89 0.295063Z"
							fill="currentColor"
						/>
					</svg>
				</span>
			</span>
		)}
		{label}
	</label>
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const triggerButtonStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	boxSizing: 'border-box',
	width: '100%',
	minWidth: '160px',
	maxWidth: '368px',
	height: token('space.500'),
	padding: '4px 16px',
	gap: token('space.200'),
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light400')}`,
	borderRadius: '20px',
	color: wacToken('color.primary.black'),
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	fontWeight: 400,
	lineHeight: '24px',
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '4px',
	},
	[media.below.sm]: {
		minWidth: 0,
		maxWidth: 'none',
	},
});

const labelTextStyle = css({
	// The Figma spec keeps the label on one line and truncates it with an ellipsis.
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const labelGroupStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
});

const triggerButtonOpenStyle = css({
	border: `2px solid ${wacToken('color.primary.blue')}`,
});

const activeCountBadgeStyle = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: '20px',
	height: '20px',
	borderRadius: '50%',
	backgroundColor: wacToken('color.primary.blue'),
	color: wacToken('color.neutral.light0'),
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	fontWeight: 700,
	lineHeight: '20px',
});

const dropdownArrowStyle = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '14px',
	height: '9px',
	flexShrink: 0,
	color: wacToken('color.primary.black'),
	transition: 'transform 0.2s ease',
});

const dropdownArrowRotatedStyle = css({
	transform: 'rotate(180deg)',
});

const panelStyle = css({
	position: 'absolute',
	boxSizing: 'border-box',
	minWidth: '240px',
	overflow: 'hidden',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light200')}`,
	borderRadius: '4px',
	zIndex: 2,
	insetBlockStart: '55px',
	padding: token('space.100'),
	boxShadow: token('elevation.shadow.overlay'),
});

const optionListStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
});

const optionLabelStyle = css({
	boxSizing: 'border-box',
	width: '100%',
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	padding: '4px 8px',
	borderRadius: '8px',
});

const checkboxStyle = css({
	width: '18px',
	height: '18px',
	borderRadius: '3px',
	border: `2px solid ${wacToken('color.primary.black')}`,
	flexBasis: '18px',
	flexShrink: 0,
	marginInlineEnd: token('space.100'),
	background: wacToken('color.neutral.light0'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	':hover': {
		border: `2px solid ${wacToken('color.primary.blue')}`,
	},
	'&.checked': {
		background: wacToken('color.primary.blue'),
		border: `2px solid ${wacToken('color.primary.blue')}`,
	},
});

const checkmarkStyle = css({
	display: 'inline-flex',
	color: wacToken('color.neutral.light0'),
	opacity: 0,
});

const checkmarkVisibleStyle = css({
	opacity: 1,
});

const hiddenCheckboxStyle = css({
	// Visually hidden but kept focusable; the visible sibling box shows the focus ring.
	position: 'absolute',
	opacity: 0,
	width: '20px',
	height: '20px',
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus-visible + span': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
});
